import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subMonths, format, differenceInDays, parseISO, startOfMonth } from "date-fns";

export interface TrialRecord {
  id: string;
  student_name: string | null;
  student_nickname: string | null;
  student_firstname: string | null;
  student_lastname: string | null;
  grade: string | null;
  class_date: string | null;
  location: string | null;
  status: string | null;
  consultant_teacher: string | null;
  evaluator_teacher: string | null;
  teacher_code: string | null;
  closed_success_at: string | null;
  order_code: string | null;
}

interface OrderOverviewRow {
  order_code: string;
  created_at: string | null;
}

const SUCCESS_STATUSES = ["ปิดการขายเรียบร้อย"];
const FAILURE_STATUSES = [
  "ปิดการขายไม่สำเร็จ",
  "ทดลองแล้วลูกค้าไม่สนใจ",
  "ยกเลิกการทดลองเรียน",
  "ลูกค้าไม่สนใจ (รอปรึกษาและตัดจากที่กัพ)",
];
const REFUND_STATUSES = ["ยกเลิกทดลองเรียนแต่ให้ครู"];

export function categorizeStatus(status: string | null) {
  if (!status) return "in_progress";
  if (SUCCESS_STATUSES.includes(status)) return "success";
  if (FAILURE_STATUSES.includes(status)) return "failure";
  if (REFUND_STATUSES.includes(status)) return "refund";
  return "in_progress";
}

export interface TeacherStat {
  teacherCode: string;
  teacherName: string;
  teacherNickname: string;
  total: number;
  success: number;
  failure: number;
  refund: number;
  inProgress: number;
  conversionRate: number;
  trials: TrialRecord[];
}

export interface BranchStat {
  branch: string;
  total: number;
  success: number;
  failure: number;
  refund: number;
}

export interface ConversionEntry {
  teacherCode: string;
  days: number;
}

export interface MonthlyTrend {
  month: string;
  success: number;
  failure: number;
  refund: number;
  inProgress: number;
}

export function useClassTrialStats(dateFrom: Date, dateTo: Date, branch: string) {
  return useQuery({
    queryKey: ["class-trial-stats", format(dateFrom, "yyyy-MM-dd"), format(dateTo, "yyyy-MM-dd"), branch],
    queryFn: async () => {
      let query = supabase
        .from("class_trials")
        .select("id, student_name, student_nickname, student_firstname, student_lastname, grade, class_date, location, status, consultant_teacher, evaluator_teacher, teacher_code, closed_success_at, order_code")
        .gte("class_date", format(dateFrom, "yyyy-MM-dd"))
        .lte("class_date", format(dateTo, "yyyy-MM-dd"));

      if (branch && branch !== "ทุกสาขา") {
        query = query.eq("location", branch);
      }

      const { data: teacherList, error: teacherListError } = await supabase.schema("teacher" as any)
        .from("teachers")
        .select("code, name, nickname");
      if (teacherListError) throw teacherListError;
      const teacherListMap = new Map(teacherList?.map((t) => [t.code, { name: t.name, nickname: t.nickname }]));

      const { data: trials, error } = await query;
      if (error) throw error;

      // Fetch order_overview created_at for conversion time fallback
      const orderCodes = (trials || [])
        .filter((t) => t.order_code && categorizeStatus(t.status) === "success")
        .map((t) => t.order_code!);

      let orderMap: Record<string, string> = {};
      if (orderCodes.length > 0) {
        const uniqueCodes = [...new Set(orderCodes)];
        // Batch fetch in chunks of 50
        for (let i = 0; i < uniqueCodes.length; i += 50) {
          const chunk = uniqueCodes.slice(i, i + 50);
          const { data: orders } = await supabase
            .from("order_overview" as any)
            .select("order_code, created_at")
            .in("order_code", chunk);
          if (orders) {
            (orders as any[]).forEach((o: OrderOverviewRow) => {
              if (o.order_code && o.created_at) {
                orderMap[o.order_code] = o.created_at;
              }
            });
          }
        }
      }

      const records = (trials || []) as TrialRecord[];

      // Summary counts
      let totalCount = records.length;
      let successCount = 0;
      let failureCount = 0;
      let refundCount = 0;
      let inProgressCount = 0;

      records.forEach((r) => {
        const cat = categorizeStatus(r.status);
        if (cat === "success") successCount++;
        else if (cat === "failure") failureCount++;
        else if (cat === "refund") refundCount++;
        else inProgressCount++;
      });

      const decided = successCount + failureCount;
      const conversionRate = decided > 0 ? (successCount / decided) * 100 : 0;
      const decisionRate = totalCount > 0 ? (decided / totalCount) * 100 : 0;

      // Monthly trends
      const monthMap: Record<string, MonthlyTrend> = {};
      records.forEach((r) => {
        if (!r.class_date) return;
        const monthKey = format(startOfMonth(parseISO(r.class_date)), "yyyy-MM");
        if (!monthMap[monthKey]) {
          monthMap[monthKey] = { month: monthKey, success: 0, failure: 0, refund: 0, inProgress: 0 };
        }
        const cat = categorizeStatus(r.status);
        if (cat === "success") monthMap[monthKey].success++;
        else if (cat === "failure") monthMap[monthKey].failure++;
        else if (cat === "refund") monthMap[monthKey].refund++;
        else monthMap[monthKey].inProgress++;
      });
      const monthlyTrends = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));

      // Conversion time
      const conversionEntries: ConversionEntry[] = [];
      records.forEach((r) => {
        if (categorizeStatus(r.status) !== "success" || !r.class_date) return;
        const closedDate = r.closed_success_at || (r.order_code ? orderMap[r.order_code] : null);
        if (!closedDate) return;
        const days = differenceInDays(parseISO(closedDate), parseISO(r.class_date));
        if (days >= 0) {
          conversionEntries.push({ teacherCode: r.teacher_code || r.evaluator_teacher || "ไม่ระบุ", days });
        }
      });

      const avgDays = conversionEntries.length > 0
        ? conversionEntries.reduce((s, e) => s + e.days, 0) / conversionEntries.length
        : 0;
      const minDays = conversionEntries.length > 0 ? Math.min(...conversionEntries.map((e) => e.days)) : 0;
      const maxDays = conversionEntries.length > 0 ? Math.max(...conversionEntries.map((e) => e.days)) : 0;

      // Conversion buckets
      const buckets = { within7: 0, week1to2: 0, week2to4: 0, overMonth: 0 };
      conversionEntries.forEach((e) => {
        if (e.days <= 7) buckets.within7++;
        else if (e.days <= 14) buckets.week1to2++;
        else if (e.days <= 30) buckets.week2to4++;
        else buckets.overMonth++;
      });

      // Teacher conversion speed
      const teacherConvMap: Record<string, number[]> = {};
      conversionEntries.forEach((e) => {
        if (!teacherConvMap[e.teacherCode]) teacherConvMap[e.teacherCode] = [];
        teacherConvMap[e.teacherCode].push(e.days);
      });
      const teacherConversionSpeed = Object.entries(teacherConvMap)
        .map(([code, days]) => ({
          teacherCode: code,
          teacherName: teacherListMap.get(code)?.name?.trim() || "",
          teacherNickname: teacherListMap.get(code)?.nickname?.trim() || "",
          count: days.length,
          avg: days.reduce((s, d) => s + d, 0) / days.length,
          min: Math.min(...days),
          max: Math.max(...days),
        }))
        .sort((a, b) => a.avg - b.avg);

      // Teacher stats
      const teacherMap: Record<string, TeacherStat> = {};
      records.forEach((r) => {
        const code = r.teacher_code || r.evaluator_teacher || "ไม่ระบุ";
        const teacherInfo = teacherListMap.get(code);
        if (!teacherMap[code]) {
          teacherMap[code] = { teacherCode: code, teacherName: teacherInfo?.name?.trim() || "", teacherNickname: teacherInfo?.nickname?.trim() || "", total: 0, success: 0, failure: 0, refund: 0, inProgress: 0, conversionRate: 0, trials: [] };
        }
        teacherMap[code].total++;
        const cat = categorizeStatus(r.status);
        if (cat === "success") teacherMap[code].success++;
        else if (cat === "failure") teacherMap[code].failure++;
        else if (cat === "refund") teacherMap[code].refund++;
        else teacherMap[code].inProgress++;
        teacherMap[code].trials.push(r);
      });
      const teacherStats = Object.values(teacherMap).map((t) => {
        const d = t.success + t.failure;
        t.conversionRate = d > 0 ? (t.success / d) * 100 : 0;
        return t;
      }).sort((a, b) => b.total - a.total);


      // Branch stats
      const branchMap: Record<string, BranchStat> = {};
      records.forEach((r) => {
        const loc = r.location || "ไม่ระบุ";
        if (!branchMap[loc]) {
          branchMap[loc] = { branch: loc, total: 0, success: 0, failure: 0, refund: 0 };
        }
        branchMap[loc].total++;
        const cat = categorizeStatus(r.status);
        if (cat === "success") branchMap[loc].success++;
        else if (cat === "failure") branchMap[loc].failure++;
        else if (cat === "refund") branchMap[loc].refund++;
      });
      const branchStats = Object.values(branchMap).sort((a, b) => b.total - a.total);

      // Distinct branches for filter
      const branches = [...new Set(records.map((r) => r.location).filter(Boolean))] as string[];

      return {
        records,
        totalCount,
        successCount,
        failureCount,
        refundCount,
        inProgressCount,
        conversionRate,
        decisionRate,
        monthlyTrends,
        conversionEntries,
        avgDays,
        minDays,
        maxDays,
        buckets,
        teacherConversionSpeed,
        teacherStats,
        branchStats,
        branches,
      };
    },
  });
}
